import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNostr } from './useNostr';
import { useCurrentUser } from './useCurrentUser';
import { useAuthor } from './useAuthor';
import { useToast } from './useToast';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to retrieve Lightning zap metadata or receipts, and generate real NIP-57 lightning invoices
 */
export function useZaps(targetEvent: NostrEvent) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const targetAuthorPubkey = targetEvent.pubkey;
  const author = useAuthor(targetAuthorPubkey);

  // Aggregate zap counts/receipts (kind 9735)
  const { data: zapReceipts = [], isLoading: isReceiptsLoading } = useQuery({
    queryKey: ['zapReceipts', targetEvent.id],
    queryFn: async () => {
      if (!nostr) return [];
      return nostr.query([{ kinds: [9735], '#e': [targetEvent.id] }]);
    },
    enabled: !!nostr,
  });

  // Calculate real totalSats summation from receipts (parse bolt11 or description tags)
  const totalSats = zapReceipts.reduce((acc, receipt) => {
    try {
      // Find 'description' tag (contains stringified original zap request JSON)
      const descTag = receipt.tags.find(t => t[0] === 'description');
      if (descTag && descTag[1]) {
        const originalReq = JSON.parse(descTag[1]);
        const amountMsat = parseInt(originalReq.amount || '0', 10);
        if (amountMsat > 0) return acc + Math.floor(amountMsat / 1000);
      }

      // Fallback: parse bolt11 amount estimation if description JSON fails
      const bolt11Tag = receipt.tags.find(t => t[0] === 'bolt11');
      if (bolt11Tag && bolt11Tag[1]) {
        // Simple bolt11 quantity extraction: lnbc21n1p... -> parses out '21'
        const match = bolt11Tag[1].match(/^lnbc(\d+)([pnde])/i);
        if (match && match[1]) {
          const amt = parseInt(match[1], 10);
          return acc + amt;
        }
      }
      return acc + 21; // Preset fallback mockup default if unresolvable
    } catch {
      return acc;
    }
  }, 0);

  // Helper resolving lud16 -> well-known LNURL endpoint URL
  const resolveLud16Url = (lud16Email: string): string => {
    const parts = lud16Email.split('@');
    if (parts.length !== 2) throw new Error('Invalid Lightning address format.');
    return `https://${parts[1]}/.well-known/lnurlp/${parts[0]}`;
  };

  // Generate real NIP-57 Lightning / Zap Invoice supporting direct-to-CORS-proxy fallbacks
  const createInvoice = useMutation({
    mutationFn: async ({ amount, comment }: { amount: number; comment?: string }) => {
      if (!nostr) throw new Error('Nostr connection required.');
      if (!user) throw new Error('Sign-in required to generate tipping invoices.');

      if (!author.data?.metadata) {
        throw new Error('Could not resolve creator profile metadata.');
      }

      const { lud16, lud06 } = author.data.metadata;
      if (!lud16 && !lud06) {
        throw new Error('Creator profile missing Lightning details (lud16/lud06).');
      }

      const address = (lud16 || lud06 || '').trim();

      // Step 1: Query the user's LNURL metadata endpoint
      let lnurlEndpoint = lud16 ? resolveLud16Url(address) : address;

      const fetchEndpointWithFallback = async (url: string) => {
        try {
          const r = await fetch(url);
          if (!r.ok) throw new Error();
          return await r.json();
        } catch {
          // CORS fallback retry
          const proxyUrl = `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;
          const r = await fetch(proxyUrl);
          if (!r.ok) throw new Error('Could not retrieve LNURL profile details even via proxy bypass.');
          return await r.json();
        }
      };

      const lnurlMeta = await fetchEndpointWithFallback(lnurlEndpoint);

      const callback = lnurlMeta.callback;
      if (!callback) {
        throw new Error('LNURL target rejects invoice callouts.');
      }

      const amountMsats = amount * 1000;

      // Check min/max sendable limits (if returned by provider)
      if (lnurlMeta.minSendable) {
        const minSats = Math.floor(parseInt(lnurlMeta.minSendable, 10) / 1000);
        if (amount < minSats) throw new Error(`Amount is under provider minimum of ${minSats} sats.`);
      }
      if (lnurlMeta.maxSendable) {
        const maxSats = Math.floor(parseInt(lnurlMeta.maxSendable, 10) / 1000);
        if (amount > maxSats) throw new Error(`Amount is over provider maximum of ${maxSats} sats.`);
      }

      // Step 2: Build NIP-57 kind-9734 Zap Request MANUALLY
      // Avoids brittle library-specific 'makeZapRequest' signature discrepancies
      const now = Math.floor(Date.now() / 1000);
      const zapRequestTags = [
        ['relays', 'wss://relay.ditto.pub', 'wss://relay.primal.net', 'wss://relay.damus.io'],
        ['amount', String(amountMsats)],
        ['p', targetAuthorPubkey],
        ['e', targetEvent.id]
      ];

      const zapRequest = {
        kind: 9734,
        content: comment || '',
        tags: zapRequestTags,
        created_at: now
      };

      if (!user.signer) {
        throw new Error('No active keys signer extension available.');
      }

      const signedRequest = await user.signer.signEvent(zapRequest);

      // Step 3: Fetch the actual BOLT11 from callback provider
      const finalInvoiceUrl = `${callback}?amount=${amountMsats}&nostr=${encodeURIComponent(JSON.stringify(signedRequest))}`;
      
      const fetchInvoiceWithFallback = async (url: string) => {
        try {
          const r = await fetch(url);
          if (!r.ok) throw new Error();
          return await r.json();
        } catch {
          const proxyUrl = `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;
          const r = await fetch(proxyUrl);
          if (!r.ok) throw new Error('Failed to fetch invoice callback even via CORS proxy fallback.');
          return await r.json();
        }
      };

      const invoiceData = await fetchInvoiceWithFallback(finalInvoiceUrl);
      const pr = invoiceData.pr;

      if (!pr || typeof pr !== 'string') {
        throw new Error('Lightning provider failed to deliver a valid BOLT11 invoice.');
      }

      return {
        invoice: pr,
        amount,
        recipientPubkey: targetAuthorPubkey
      };
    }
  });

  return {
    zapCount: zapReceipts.length,
    totalSats,
    createInvoice,
    isReceiptsLoading
  };
}
export default useZaps;
